import React from 'react'
import * as rn from 'react-native'

import * as c from '../../../common'
import * as r from '../../../react-utils'

// import * as m from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import Icon from 'react-native-vector-icons/MaterialCommunityIcons'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Modal from '@mui/material/Modal'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import InputBase from '@mui/material/InputBase'
import styled from '@mui/material/styles/styled'
import { SxProps, Theme, Typography } from '@mui/material'

import Pad from '../../comps/Pad'
import * as cp from '../../../app/components'
import * as gs from '../../../app/gStyles'
import { FormatPrice as formatPrice } from '../../helpers/FormatPrice'

import styles from './CumTable.module.css'

export type CumTableProps = c.r<{
  cashPlusPower: c.numbers
  customerID: string
  loanPlusPower: c.numbers
  ppaYearlyTotals: c.numbers
}>

export default React.memo<CumTableProps>(function CumTable({
  cashPlusPower,
  customerID,
  loanPlusPower,
  ppaYearlyTotals,
}) {
  //#region global
  const [solarCompany] = r.useCustomerField(customerID, 'solarCompany')
  //#endregion global
  //#region local
  const [years, , handleYears] = r.useInputVal('2')

  const cashPlusPowerTotal = cashPlusPower[cashPlusPower.length - 1] || 0
  const cppAvgYear1 = Math.round((cashPlusPower[0] || 0) / 12)
  const cppAvgLifetime = Math.round(cashPlusPowerTotal / 25 / 12)
  const loanPlusPowerTotal = loanPlusPower[loanPlusPower.length - 1] || 0
  const lppAvgYear1 = Math.round((loanPlusPower[0] || 0) / 12)
  const lppAvgLifetime = Math.round(loanPlusPowerTotal / 25 / 12)
  const ppaTotal = ppaYearlyTotals[ppaYearlyTotals.length - 1] || 0
  const ppaAvgYear1 = Math.round((ppaYearlyTotals[0] || 0) / 12)
  const ppaAvgLifetime = Math.round(ppaTotal / ppaYearlyTotals.length / 12)

  const [highlightMode, setHighlightMode] = React.useState<
    'lifetime' | 'firstYear'
  >('firstYear')
  const handleLifetimePress = React.useCallback(
    () => void setHighlightMode('lifetime'),
    [],
  )
  const handleFirstYearPress = React.useCallback(
    () => void setHighlightMode('firstYear'),
    [],
  )

  const bestChoice = ((): 'cpp' | 'lpp' | 'ppa' => {
    if (highlightMode === 'firstYear') {
      const [_bestChoice] = [
        { label: 'cpp' as const, value: cppAvgYear1 },
        { label: 'lpp' as const, value: lppAvgYear1 },
        { label: 'ppa' as const, value: ppaAvgYear1 },
      ].sort((a, b) => a.value - b.value)

      return _bestChoice!.label
    }

    const [_bestChoice] = [
      { label: 'cpp' as const, value: cppAvgLifetime },
      { label: 'lpp' as const, value: lppAvgLifetime },
      { label: 'ppa' as const, value: ppaAvgLifetime },
    ].sort((a, b) => a.value - b.value)

    return _bestChoice!.label
  })()
  const cppBest = bestChoice === 'cpp'
  const lppBest = bestChoice === 'lpp'
  const ppaBest = bestChoice === 'ppa'
  const [open, , toggleOpen] = r.useBool(false)
  React.useEffect(() => {
    // No cleanup here left <body class="no-scroll"> stuck for the whole report
    // if this unmounted while `open` (same leak as B150 Modal). The old
    // addEventListener/removeEventListener pair ran on the same tick - dead
    // code, never an active listener - so it is dropped, not switched on.
    if (!open) {
      return undefined
    }

    document.body.classList.add('no-scroll')

    return (): void => {
      document.body.classList.remove('no-scroll')
    }
  }, [open])
  //#endregion local
  //#region styling
  const StyledSelect = React.useMemo(() => {
    return styled(InputBase)(({ theme }) => ({
      'label + &': {
        marginTop: theme.spacing(3),
      },
      '& .MuiInputBase-input': {
        backgroundColor: theme.palette.background.paper,
        border: '1px solid #ced4da',
        borderRadius: 4,
        fontFamily: [
          '-apple-system',
          '"Apple Color Emoji"',
          '"Helvetica Neue"',
          '"Segoe UI Emoji"',
          '"Segoe UI Symbol"',
          '"Segoe UI"',
          'Arial',
          'BlinkMacSystemFont',
          'Roboto',
          'sans-serif',
        ].join(','),
        fontSize: 16,
        padding: '10px 26px 10px 12px',
        position: 'relative',
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        // Use the system font instead of the default Roboto font.

        '&:focus': {
          borderColor: c.companyColors[solarCompany],
          borderRadius: 1,
          boxShadow: `0 0 0 1px ${c.companyColors[solarCompany]}`,
        },
      },
    }))
  }, [solarCompany])
  const toggleActiveStyle = React.useMemo(
    () => ({
      ...(highlightMode === 'firstYear'
        ? sx.toggleHalfLeft
        : sx.toggleHalfRight),
      backgroundColor: c.companyColors[solarCompany],
    }),
    [highlightMode, solarCompany],
  )
  //#endregion styling

  const buttonModalStyle = React.useMemo((): SxProps<Theme> => {
    return {
      alignSelf: 'center',
      backgroundColor: c.companyColors[solarCompany],
    }
  }, [solarCompany])

  const tableContainerStyle = React.useMemo((): SxProps<Theme> => {
    return {
      maxHeight: '380px',
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: c.companyColors[solarCompany],
      },
      minWidth: '600px',
      paddingRight: c.parseInt(years) > 5 ? 'unset' : '10px',
    }
  }, [solarCompany, years])

  return (
    <React.Fragment>
      <cp.View style={gs.colCentered}>
        <rn.View style={sx.toggle}>
          <rn.Pressable
            onPress={handleFirstYearPress}
            style={
              highlightMode === 'firstYear'
                ? toggleActiveStyle
                : sx.toggleHalfLeft
            }
          >
            <cp.Text
              colorScheme={highlightMode === 'firstYear' ? 'dark' : 'light'}
            >
              First Year
            </cp.Text>
          </rn.Pressable>

          <rn.Pressable
            onPress={handleLifetimePress}
            style={
              highlightMode === 'lifetime'
                ? toggleActiveStyle
                : sx.toggleHalfRight
            }
          >
            <cp.Text
              colorScheme={highlightMode === 'lifetime' ? 'dark' : 'light'}
            >
              Lifetime
            </cp.Text>
          </rn.Pressable>
        </rn.View>

        {gs.rowGap12}

        <div className={styles['table']}>
          <div className={styles['row']}>
            <div className={styles['title-row']}>Cash+Electricity</div>
            <div className={cppBest ? styles['input-green'] : styles['input']}>
              {formatPrice(
                highlightMode === 'firstYear' ? cppAvgYear1 : cppAvgLifetime,
                '$',
              )}
            </div>
          </div>

          <div className={styles['row']}>
            <div className={styles['title-row']}>Finance+Electricity</div>
            <div className={lppBest ? styles['input-green'] : styles['input']}>
              {formatPrice(
                highlightMode === 'firstYear' ? lppAvgYear1 : lppAvgLifetime,
                '$',
              )}
            </div>
          </div>

          {c.CODENAME !== 'nirvana' && (
            <div className={styles['row']}>
              <div className={styles['title-row']}>PPA</div>
              <div
                className={ppaBest ? styles['input-green'] : styles['input']}
              >
                {formatPrice(
                  highlightMode === 'firstYear' ? ppaAvgYear1 : ppaAvgLifetime,
                  '$',
                )}
              </div>
            </div>
          )}
        </div>

        <cp.View style={gs.rowCentered}>
          <Icon color="#BBB" name="information-outline" />
          {gs.colGap8}
          <cp.Text style={sx.faintText}>
            {highlightMode === 'firstYear'
              ? 'Avg monthly payments for the first year'
              : `Avg monthly payment throughout lifetime of loan${
                  c.CODENAME === 'nirvana' ? '' : '/PPA'
                }`}
          </cp.Text>
        </cp.View>

        {gs.rowGap16}

        <Button onClick={toggleOpen} sx={buttonModalStyle} variant="contained">
          SHOW COMPARISON
        </Button>
      </cp.View>

      <Modal open={open} onClose={toggleOpen}>
        <Box component={Paper} sx={sx['modalBox']}>
          <HighlightOffIcon sx={sx['closeModalIcon']} onClick={toggleOpen} />

          <InputLabel id="cum-table-years-header" sx={sx['labelYears']}>
            Years
          </InputLabel>
          <Select
            input={<StyledSelect />}
            labelId="cum-table-years-header"
            onChange={handleYears}
            sx={sx['formControl']}
            value={years.toString()}
          >
            <MenuItem value={2}>2</MenuItem>
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
          </Select>

          <Pad amt={24} />

          <TableContainer
            component={Paper}
            elevation={5}
            sx={tableContainerStyle}
          >
            <Table stickyHeader sx={sx['table']}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={sx['tableCell']}>
                    Year
                  </TableCell>
                  <TableCell align="center" sx={sx['tableCell']}>
                    Cash+Electricity
                  </TableCell>
                  <TableCell align="center" sx={sx['tableCell']}>
                    Finance+Electricity
                  </TableCell>
                  {c.CODENAME !== 'nirvana' && (
                    <TableCell align="center" sx={sx['tableCell']}>
                      PPA
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {new Array(c.parseInt(years)).fill(0).map((_, i) => (
                  <TableRow key={years + '>' + i}>
                    <TableCell align="center" sx={sx['tableCell']}>
                      {i + 1}
                    </TableCell>
                    <TableCell align="center" sx={sx['tableCell']}>
                      {formatPrice(Math.round(cashPlusPower[i] || 0), '$')}
                    </TableCell>
                    <TableCell align="center" sx={sx['tableCell']}>
                      {formatPrice(
                        i >= loanPlusPower.length
                          ? 0
                          : Math.round(loanPlusPower[i] || 0),
                        '$',
                      )}
                    </TableCell>
                    {c.CODENAME !== 'nirvana' && (
                      <TableCell
                        align="center"
                        sx={true ? sx['tableCellGreen'] : sx['tableCell']}
                      >
                        {formatPrice(Math.round(ppaYearlyTotals[i] || 0), '$')}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {gs.rowGap16}

          <cp.View style={gs.rowCentered}>
            <Icon color="#AAA" name="information-outline" />
            {gs.colGap8}
            <cp.Text style={sx.faintText}>Amounts are cumulative</cp.Text>
          </cp.View>

          <TableContainer>
            <Table sx={sx['tableTotal']}>
              <TableHead>
                <TableRow sx={sx['tableRowTotal']}>
                  <TableCell
                    align="center"
                    sx={sx['tableCellHiddenTotal']}
                  ></TableCell>

                  <TableCell align="center" sx={sx['tableCellTotal']}>
                    <Typography sx={sx['totalText']}>Total:</Typography>
                    <span>
                      {formatPrice(Math.round(cashPlusPowerTotal), '$')}
                    </span>
                  </TableCell>
                  <TableCell align="center" sx={sx['tableCellTotal']}>
                    <Typography sx={sx['totalText']}>Total:</Typography>
                    <span>
                      {formatPrice(Math.round(loanPlusPowerTotal), '$')}
                    </span>
                  </TableCell>
                  {c.CODENAME !== 'nirvana' && (
                    <TableCell align="center" sx={sx['tableCellTotal']}>
                      <Typography sx={sx['totalText']}>Total:</Typography>
                      <span>{formatPrice(Math.round(ppaTotal), '$')}</span>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
        </Box>
      </Modal>
    </React.Fragment>
  )
})

const toggleHalfBase = {
  paddingHorizontal: 32,
  paddingVertical: 8,
}
const sx = {
  closeModalIcon: {
    cursor: 'pointer',
    position: 'absolute',
    right: 10,
    top: 10,
  },
  faintText: { color: '#AAA', fontFamily: c.light.fontFamily },
  formControl: { width: '100px' },
  labelYears: { fontSize: '18px', textAlign: 'center' },
  modalBox: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    left: '50%',
    padding: '20px',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  },
  table: { boxSizing: 'border-box', margin: 0, minWidth: '580px' },
  tableCell: { fontSize: '22px' },
  tableCellGreen: { color: 'green', fontSize: '22px' },
  tableTotal: { width: '580px' },
  tableRowTotal: { display: 'flex', justifyContent: 'space-between' },
  tableCellHiddenTotal: {
    fontSize: '22px',
    visibility: 'hidden',
    width: '70px',
  },
  tableCellTotal: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '22px',
  },
  toggle: {
    ...gs.rowSpaceBetween,
    borderColor: '#AAA',
    borderRadius: 10,
    borderWidth: 1,
  } as const,
  toggleHalfLeft: {
    ...toggleHalfBase,
    borderBottomleftRadius: 10,
    borderTopLeftRadius: 10,
  },
  toggleHalfRight: {
    ...toggleHalfBase,
    borderTopRightRadius: 10,
    borderBottomRightRadius: 10,
  },
  totalText: { fontSize: '22px', fontWeight: '700' },
}
